// Change REACT_APP_DATA_BASE_URL to point to an alternative asset root/CDN.
const BASE = (
  process.env.REACT_APP_DATA_BASE_URL || `${process.env.PUBLIC_URL || ''}/assets`
).replace(/\/$/, '');
const PUBLIC_DATA_BASE = BASE.replace(/\/assets$/, '');
const SKILL_DATA_ROOT = 'skill-path';
const COMMON_DATA_ROOT = 'data-common';
const EXAM_DATA_ROOT = 'exam';
const CONFIG_ROOT = 'data-config';
const TOPIC_LESSON_ROOT = 'data-topic-lessons';
const IMAGES_ROOT = 'data-images';
// Recorded audio-lecture phrase libraries, one playlist.json + mp3 set per
// target-language code — separate from the assets/audio TTS-driven clip
// index (see AudioSlideshowPage's assetUrl). Only a subset of target
// languages have this content recorded — see audioLectures.js.
const DATA_AUDIO_ROOT = 'data-audio';
// common-foundations and user-journey are shared identically across every
// curriculum, not per-target-home content, so they live once under
// data-common instead of being duplicated into each skill-path home.
const COMMON_DATA_FAMILIES = ['i18n', 'language-introductions', 'common-foundations', 'user-journey', 'speech_components'];

// skill-path now lives once per curriculum home (data-topic-lessons/{home}/skill-path),
// one full copy per target language so each can diverge independently later.
// Used when no curriculum home is selected yet (e.g. nav/UI chrome rendered before a target
// language is chosen) — content is identical across homes today, so 'en' is a safe anchor.
const DEFAULT_SKILL_HOME = 'en';

// These modules were consolidated from many small per-topic files into
// {module}/{locale}.manifest.json + {module}/{locale}.partN.json bundles
// (see scripts/bundle-skill-path-locales.mjs). Everything else under
// skill-path (module-root index.json/schema) is still one file per
// path and resolves directly.
const BUNDLED_SKILL_MODULES = new Set([]);

const RETRY_COUNT = 2;
const RETRY_DELAY_MS = 1000;
const REQUEST_TIMEOUT_MS = 10 * 1000;
const MEMORY_TTL_MS = 5 * 60 * 1000;
const memoryCache = new Map();
const inFlight = new Map();

export function dataUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  if (cleanPath === 'exercise' || cleanPath.startsWith('exercise/')) {
    const examPath = cleanPath.replace(/^exercise\/?/, '');
    return `${PUBLIC_DATA_BASE}/${EXAM_DATA_ROOT}/${examPath}`.replace(/\/$/, '');
  }
  const isCommonData = COMMON_DATA_FAMILIES.some(
    (family) => cleanPath === family || cleanPath.startsWith(`${family}/`)
  );
  const root = isCommonData ? COMMON_DATA_ROOT : SKILL_DATA_ROOT;
  return `${PUBLIC_DATA_BASE}/${root}/${cleanPath}`;
}

// Small, shared configuration files live outside the lesson-data trees so both
// web and mobile can resolve them directly from <baseUrl>/data-config.
export function configDataUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${CONFIG_ROOT}/${cleanPath}`;
}

export function curriculumDataUrl(homeLang, path) {
  const cleanPath = path.replace(/^\//, '');
  const home = homeLang || DEFAULT_SKILL_HOME;
  // skill-path/ no longer exists as its own directory — every module that
  // used to live under {home}/skill-path/ was moved up to sit directly
  // under {home}/, alongside conversation/ and exam/.
  return `${PUBLIC_DATA_BASE}/${TOPIC_LESSON_ROOT}/${home}/${cleanPath}`;
}

export function topicLessonsDataUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${TOPIC_LESSON_ROOT}/${cleanPath}`;
}

// Shared, lesson-authored images (referenced from lesson JSON as
// site-root-relative paths like "/data-images/vocabulary/...") live in their
// own tree rather than under a lesson's own language/folder — see the
// mobile app's matching TopicLessonsService.commonAssetUrl.
export function dataImagesUrl(path) {
  const cleanPath = path.replace(/^\//, '').replace(new RegExp(`^${IMAGES_ROOT}/`), '');
  return `${PUBLIC_DATA_BASE}/${IMAGES_ROOT}/${cleanPath}`;
}

// writing/stories/basic-phrases-sentences/thematic-vocabulary/
// wordclass-vocabulary each keep one document per locale code (any of the
// ~40 supported codes) under data-common/{category}/{code}/..., holding
// that locale's own content — used interchangeably for the "target"
// curriculum role and the "native"/reader role, since a locale's content no
// longer depends on which curriculum home it's browsed from (the physical
// file used to live duplicated under every home's own {category}/, one full
// copy per home even though the bytes were identical). Defaults to
// index.json — the one file every category keeps per locale; basic-phrases-
// sentences additionally keeps a per-home pronunciation.json alongside it.
// conversation used to follow this same convention but has since moved to
// its own per-level layout — see conversationLevelUrl.
export function categoryLocaleUrl(category, code, path = 'index.json') {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${COMMON_DATA_ROOT}/${category}/${code}/${cleanPath}`;
}

// conversation and stories are both organized per level (conversation:
// a1/a2/b1/b2; stories: easy/medium/difficult — see each level's
// catalog.json), each level holding a handful of items at {category}/
// {level}/{item}.json — an English-anchored root wrapped as
// { [singular]: {...} } — plus {category}/{level}/native/{code}/{item}.json
// translation overlays (thin, or fully re-authored) for that same item —
// see conversationV2.js's mergeChapterOverlay / storiesV2.js.
export function categoryLevelUrl(category, level, path) {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${COMMON_DATA_ROOT}/${category}/${level}/${cleanPath}`;
}

// writing/thematic-vocabulary/wordclass-vocabulary each keep one shared,
// locale-independent document (an English-anchored skeleton and, for the
// vocabulary categories, a topic/group catalog) alongside their per-locale
// folders. Filenames are underscore-prefixed so they can never collide with
// a locale code.
export function categorySharedUrl(category, filename) {
  return `${PUBLIC_DATA_BASE}/${COMMON_DATA_ROOT}/${category}/${filename}`;
}

// Each exam home ({home}/exam) keeps its own curriculum's root packs (examN.json,
// listed in catalog.json) directly at its root, since these can diverge per home in
// the future. Every other target-language's packs live under {home}/exam/native/{code}/
// as a bare-numbered diff (N.json) that holds only the fields differing from examN.json
// (see mergeExamPack) — the shared shape (title, xp, skills, per-question type/category/
// flags, ...) lives once at the root. catalog.json is identical for every target
// language, so it always resolves from the root regardless of the code prefix a caller
// passes. Paths outside this convention (e.g. i18n/{locale}.json) pass through unchanged.
export function examDataUrl(homeLang, path) {
  const cleanPath = path.replace(/^\//, '');
  const home = homeLang || DEFAULT_SKILL_HOME;
  const [code, ...restSegments] = cleanPath.split('/');
  const rest = restSegments.join('/');
  const bareRest = rest.replace(/\?.*$/, '');
  const isShared = bareRest === 'catalog.json' || bareRest === 'index.json';
  // A bare filename (no code prefix, e.g. fetching a home's own root pack directly)
  // is already root-relative — nothing to rewrite.
  const examPath = !code || code === 'i18n' || restSegments.length === 0
    ? cleanPath
    : code === home || isShared
      ? rest
      : `native/${cleanPath}`;
  return `${PUBLIC_DATA_BASE}/${TOPIC_LESSON_ROOT}/${home}/${EXAM_DATA_ROOT}/${examPath}`;
}

// The exam catalog ({home}/exam/catalog.json) uses the same index1/index2/… entry
// shape as grammar/alphabet/stories: each value carries `path` (the root pack file,
// examN.json) plus an `id` like `exam-N`. Returns catalog-ordered entries as
// { packId, rootFile } — packId is the bare number N, used for score/progress keys
// and for the per-language diff at native/{code}/{packId}.json.
export function parseExamCatalog(catalog) {
  return Object.entries(catalog || {})
    .map(([key, value]) => {
      const indexMatch = /^index([1-9]\d*)$/.exec(key);
      const rootFile = value && typeof value === 'object' ? value.path : null;
      if (!indexMatch || !rootFile) return null;
      const order = Number(indexMatch[1]);
      const packId = (/\d+/.exec(rootFile) || [String(order)])[0];
      return { order, packId, rootFile };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map(({ packId, rootFile }) => ({ packId, rootFile }));
}

// question/sentence/explanation/expectedText, each options[key], and each left/right
// item are themselves diffed one level deeper (only the sub-keys — typically just
// txt — that differ from root are kept), so merging them back requires the same
// per-field/per-key inheritance instead of a wholesale replace.
const EXAM_TEXT_WRAPPER_FIELDS = ['question', 'sentence', 'explanation', 'expectedText'];

function mergeExamTextWrapper(diffWrapper, rootWrapper) {
  if (diffWrapper === undefined) return rootWrapper;
  if (diffWrapper === null || typeof diffWrapper !== 'object') return diffWrapper;
  return { ...(rootWrapper && typeof rootWrapper === 'object' ? rootWrapper : {}), ...diffWrapper };
}

function mergeExamMatchSide(diffSide, rootSide) {
  if (diffSide === undefined) return rootSide;
  const rootById = new Map((rootSide || []).map((item) => [item.id, item]));
  return diffSide.map((item) => ({ ...(rootById.get(item.id) || {}), ...item }));
}

function mergeExamQuestion(diffQuestion, rootQuestion) {
  const merged = { ...rootQuestion, ...diffQuestion };
  EXAM_TEXT_WRAPPER_FIELDS.forEach((field) => {
    if (field in diffQuestion) merged[field] = mergeExamTextWrapper(diffQuestion[field], rootQuestion[field]);
  });
  if (diffQuestion.options) {
    merged.options = { ...(rootQuestion.options || {}) };
    Object.entries(diffQuestion.options).forEach(([key, diffOption]) => {
      merged.options[key] = mergeExamTextWrapper(diffOption, merged.options[key]);
    });
  }
  if (diffQuestion.left) merged.left = mergeExamMatchSide(diffQuestion.left, rootQuestion.left);
  if (diffQuestion.right) merged.right = mergeExamMatchSide(diffQuestion.right, rootQuestion.right);
  return merged;
}

// Reassembles a full exam pack from its root (shape + questions) and a native/{code}
// diff (only the fields — pack-level and per-question — that differ from root).
// Questions are matched by id; any diff question with no root match is kept as-is.
export function mergeExamPack(root, diff) {
  const rootQuestionsById = new Map((root.questions || []).map((q) => [q.id, q]));
  const questions = (diff.questions || []).map((dq) =>
    mergeExamQuestion(dq, rootQuestionsById.get(dq.id) || {})
  );
  return { ...root, ...diff, questions };
}

// mcq/match/fill_blank questions store question/sentence/explanation/expectedText
// as { txt, translate, translationHint } on disk (authoring/future-translation
// metadata), and match's left/right items the same way keyed as txt. Every other
// consumer (rendering, scoring, synthetic question generation) still expects plain
// strings, so this unwraps once at the fetch boundary instead of touching every
// call site — mirrors the topicQuestion()/_dailyTestQuestion() adapters used for
// the topic-lesson pipeline.
function unwrapExamText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt ?? null : value;
}

function unwrapExamMatchSide(side) {
  return Array.isArray(side) ? side.map((item) => ({ ...item, text: item.txt })) : side;
}

function unwrapExamQuestion(question) {
  return {
    ...question,
    question: unwrapExamText(question.question),
    sentence: unwrapExamText(question.sentence),
    explanation: unwrapExamText(question.explanation),
    expectedText: unwrapExamText(question.expectedText),
    left: unwrapExamMatchSide(question.left),
    right: unwrapExamMatchSide(question.right),
  };
}

// Fetches one exam pack for a target-language code within a home, given the root
// pack file (examN.json — possibly with a ?query suffix). When code matches the
// home, the root file already holds the full pack. Otherwise the root pack (shape)
// and the native/{code}/N.json diff (language-specific content — bare number, no
// `exam` prefix) are fetched and merged — see mergeExamPack.
export async function fetchExamPack(homeLang, code, file, options) {
  const home = homeLang || DEFAULT_SKILL_HOME;
  const root = await fetchExamData(home, file, options);
  const nativeFile = file.replace(/^exam(?=\d)/, '');
  const pack = code === home ? root : mergeExamPack(root, await fetchExamData(home, `${code}/${nativeFile}`, options));
  return { ...pack, questions: (pack.questions || []).map(unwrapExamQuestion) };
}

// common-foundations.json fields are authored as { txt, translate, translationHint }
// (same convention as topic-lessons/exam), with an English root
// (common-foundations/en/common-foundations.json) plus thin native/{code} overlays
// holding only the translated txt values, in the same shape as the field they
// replace (see topic-lessons' native-overlay pattern). Arrays of translatable
// objects (points, vowelExamples.items, ...) have no stable id, so they merge by
// position instead.
function mergeCommonFoundationsValue(rootValue, overlayValue) {
  if (overlayValue === undefined) return rootValue;
  if (Array.isArray(rootValue) && Array.isArray(overlayValue)) {
    return rootValue.map((item, i) =>
      i < overlayValue.length ? mergeCommonFoundationsValue(item, overlayValue[i]) : item
    );
  }
  if (
    rootValue && typeof rootValue === 'object' &&
    overlayValue && typeof overlayValue === 'object'
  ) {
    const merged = { ...rootValue };
    Object.keys(overlayValue).forEach((key) => {
      merged[key] = mergeCommonFoundationsValue(rootValue[key], overlayValue[key]);
    });
    return merged;
  }
  return overlayValue;
}

function unwrapCommonFoundationsValue(value) {
  if (Array.isArray(value)) return value.map(unwrapCommonFoundationsValue);
  if (value && typeof value === 'object') {
    if ('txt' in value) return value.txt ?? null;
    const unwrapped = {};
    Object.keys(value).forEach((key) => {
      unwrapped[key] = unwrapCommonFoundationsValue(value[key]);
    });
    return unwrapped;
  }
  return value;
}

// The root document is wrapped as { type: "container-row", content: {...} } —
// a marker that its sections render in row order, top to bottom — while the
// native/{code} overlays stay unwrapped (they're a plain field diff, not a
// renderable document on their own), so only the root needs unpacking here.
function unwrapContainerRow(document) {
  return document && document.type === 'container-row' && document.content
    ? document.content
    : document;
}

// Loads the shared "how humans acquire language" reference page, translated into
// nativeCode when a native/{code} overlay exists, falling back to the English
// root untranslated otherwise (returned usedLang reflects which actually applied).
export async function loadCommonFoundations(nativeCode, options) {
  const root = unwrapContainerRow(
    await fetchData('common-foundations/en/common-foundations.json', options)
  );
  if (!nativeCode || nativeCode === 'en') {
    return { data: unwrapCommonFoundationsValue(root), usedLang: 'en' };
  }
  try {
    const overlay = await fetchData(
      `common-foundations/en/native/${nativeCode}/common-foundations.json`,
      options
    );
    return { data: unwrapCommonFoundationsValue(mergeCommonFoundationsValue(root, overlay)), usedLang: nativeCode };
  } catch {
    return { data: unwrapCommonFoundationsValue(root), usedLang: 'en' };
  }
}

// speech_components.json holds the shared tongue/lips/jaw/vocalFolds/airflow
// definitions (image + info) referenced by id from every alphabet lesson item
// (item.pronunciation.components), so they're authored once instead of
// repeated per letter per language. Translated the same way as
// common-foundations: an English root plus a sparse native/{code} overlay
// carrying just the translated txt values at the same positions.
function mergeSpeechComponentsOverlay(root, overlay) {
  if (!overlay) return root;
  const categories = { ...root.categories };
  Object.entries(overlay.categories || {}).forEach(([key, value]) => {
    if (categories[key] && value?.txt) categories[key] = { ...categories[key], txt: value.txt };
  });
  const components = {};
  Object.entries(root.components).forEach(([key, def]) => {
    const overlayDef = overlay.components?.[key];
    const states = {};
    Object.entries(def.states).forEach(([stateKey, stateVal]) => {
      const overlayState = overlayDef?.states?.[stateKey];
      states[stateKey] = {
        ...stateVal,
        txt: overlayState?.txt ?? stateVal.txt,
        info: stateVal.info ? { ...stateVal.info, txt: overlayState?.info?.txt ?? stateVal.info.txt } : stateVal.info,
      };
    });
    components[key] = {
      ...def,
      label: overlayDef?.label?.txt ? { ...def.label, txt: overlayDef.label.txt } : def.label,
      states,
    };
  });
  return { categories, components };
}

export async function loadSpeechComponents(nativeCode, options) {
  const root = await fetchData('speech_components.json', options);
  if (!nativeCode || nativeCode === 'en') return root;
  try {
    const overlay = await fetchData(`speech_components/native/${nativeCode}.json`, options);
    return mergeSpeechComponentsOverlay(root, overlay);
  } catch {
    return root;
  }
}

function sortedLessonCards(lesson) {
  if (!lesson || typeof lesson !== 'object') return [];
  return Object.entries(lesson)
    .filter(([key, item]) => /^\d+$/.test(key) && item && typeof item === 'object')
    .sort(([first], [second]) => Number(first) - Number(second))
    .map(([, item]) => item);
}

// Combines common-foundations' `content.lesson` (universal, always present)
// with the target language's pronunciation-guide `lesson` (optional, now
// registered in that module's own catalog.json + lesson1.json — mirrors
// alphabet's catalog+lesson pattern, see the web repo's dev/stracture.md —
// rather than the old single pronunciation-guide.json) into one interactive
// lesson — mirrors how loadCommonFoundations already concatenates their
// `sections` onto one reference page. Cards stay in their raw
// `{txt, translate, translationHint}`-wrapped form (same as topic-lessons/
// grammar lesson cards) since that's what ReadingMaterial/topicQuestion
// expect — unlike loadCommonFoundations, this does NOT unwrap txt values.
export async function loadCommonFoundationsLessonCards(targetCode) {
  const root = unwrapContainerRow(
    await fetchData('common-foundations/en/common-foundations.json')
  );
  const items = sortedLessonCards(root.lesson);
  try {
    const guideRoot = await fetchSkillPathData(targetCode, 'pronunciation-guide/lesson1.json');
    items.push(...sortedLessonCards(guideRoot.lesson));
  } catch {
    // No pronunciation guide for this target language yet — foundations
    // cards alone still make a complete lesson.
  }
  const chapter = { id: 'common-foundations', label: 'Pronunciation Guide' };
  return { chapter, chapters: [chapter], items };
}

// Pronunciation Guide's own per-target-language chapter catalog — same
// indexN/{id,label,path,type} convention as topic-lessons/alphabet/grammar
// (see topicLessonsFromIndex in topicLessons.js), duplicated here rather
// than imported to avoid a dataApi <-> topicLessons import cycle (mirrors
// the mobile app's own CommonFoundationsService, which duplicates this same
// loop across loadPronunciationGuideCatalog and loadChapters).
export async function loadPronunciationGuideCatalog(targetCode) {
  const index = await fetchSkillPathData(targetCode, 'pronunciation-guide/catalog.json');
  const chapters = [];
  Object.entries(index || {}).forEach(([key, entry]) => {
    const match = /^index([1-9]\d*)$/.exec(key);
    if (!match || !entry || typeof entry !== 'object') return;
    const id = String(entry.id || '').trim();
    const label = String(entry.label || '').trim();
    const path = String(entry.path || '').trim();
    if (!id || !label || !path) return;
    chapters.push({
      id,
      label,
      path,
      type: String(entry.type || 'lesson').trim().toLowerCase(),
      chapterNumber: Number(match[1]),
    });
  });
  return chapters.sort((a, b) => a.chapterNumber - b.chapterNumber);
}

// A single Pronunciation Guide chapter's lesson cards — empty (not thrown)
// when this target language has no guide authored for that chapter yet, so
// callers can still show the chapter list with an empty-state lesson.
export async function loadPronunciationGuideLessonCards(targetCode, chapter, chapters) {
  let items = [];
  try {
    const guideRoot = unwrapContainerRow(
      await fetchSkillPathData(targetCode, `pronunciation-guide/${chapter.path}`)
    );
    items = sortedLessonCards(guideRoot.lesson);
  } catch {
    // No pronunciation guide for this target language yet.
  }
  return { chapter, chapters, items };
}

async function fetchWithRetry(url, attempt = 0) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null;
  try {
    const r = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!r.ok) {
      const error = new Error(`HTTP ${r.status} for ${url}`);
      error.retryable = r.status === 408 || r.status === 429 || r.status >= 500;
      throw error;
    }
    return r;
  } catch (err) {
    if (err.retryable !== false && attempt < RETRY_COUNT) {
      await new Promise((res) => setTimeout(res, RETRY_DELAY_MS * (attempt + 1)));
      return fetchWithRetry(url, attempt + 1);
    }
    throw err;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export function fetchData(path, { force = false } = {}) {
  return fetchJson(path, () => dataUrl(path), { force });
}

export function fetchConfigData(path, { force = false } = {}) {
  return fetchJson(`config:${path}`, () => configDataUrl(path), { force });
}

export function fetchCurriculumData(homeLang, path, { force = false } = {}) {
  const home = homeLang || DEFAULT_SKILL_HOME;
  return fetchJson(`curriculum:${home}:${path}`, () => curriculumDataUrl(home, path), { force });
}

function parseSkillPath(path) {
  const [moduleName, locale, ...rest] = path.replace(/^\//, '').split('/');
  return { moduleName, locale, relPath: rest.join('/') };
}

// Resolves a skill-path file for the given curriculum home. Per-locale
// index.json, module-root files, and never-bundled modules (common-foundations,
// user-journey) fetch directly; everything else looks the path up in its
// {module}/{locale}.manifest.json to find which .partN.json bundle holds it.
export async function fetchSkillPathData(homeLang, path, options = {}) {
  const { moduleName, locale, relPath } = parseSkillPath(path);
  if (!BUNDLED_SKILL_MODULES.has(moduleName) || !relPath || relPath === 'index.json') {
    return fetchCurriculumData(homeLang, path, options);
  }

  const manifest = await fetchCurriculumData(homeLang, `${moduleName}/${locale}.manifest.json`, options);
  const part = manifest.entries?.[relPath];
  if (part === undefined) {
    throw new Error(`No bundled skill-path entry for ${moduleName}/${locale}/${relPath}`);
  }

  const partData = await fetchCurriculumData(homeLang, `${moduleName}/${locale}.part${part}.json`, options);
  const entry = partData.entries?.[relPath];
  if (entry === undefined) {
    throw new Error(`Bundle part missing entry for ${moduleName}/${locale}/${relPath}`);
  }
  return entry;
}

export async function fetchSkillPathWithEnglishFallback(homeLang, pathForLocale, locale, options) {
  const code = normalizedLocale(locale);
  try {
    return await fetchSkillPathData(homeLang, pathForLocale(code), options);
  } catch (error) {
    if (code === 'en') throw error;
    return fetchSkillPathData(homeLang, pathForLocale('en'), options);
  }
}

export function fetchTopicLessonsData(path, { force = false } = {}) {
  return fetchJson(`topic-lessons:${path}`, () => topicLessonsDataUrl(path), { force });
}

export function fetchCategoryLocaleData(category, code, path = 'index.json', { force = false } = {}) {
  return fetchJson(
    `locale:${category}:${code}:${path}`,
    () => categoryLocaleUrl(category, code, path),
    { force }
  );
}

export function fetchCategoryLevelData(category, level, path, { force = false } = {}) {
  return fetchJson(
    `level:${category}:${level}:${path}`,
    () => categoryLevelUrl(category, level, path),
    { force }
  );
}

// Writing is a catalog of worked example documents (writing/catalog.json)
// rather than a per-locale or per-level tree, so it gets its own flat
// {category}/{filename} + {category}/native/{code}/{filename} builder — see
// writingV2.js.
export function writingUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${COMMON_DATA_ROOT}/writing/${cleanPath}`;
}

export function fetchWritingData(path, { force = false } = {}) {
  return fetchJson(`writing:${path}`, () => writingUrl(path), { force });
}

export function dataAudioUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  return `${PUBLIC_DATA_BASE}/${DATA_AUDIO_ROOT}/${cleanPath}`;
}

export function fetchDataAudioData(path, { force = false } = {}) {
  return fetchJson(`data-audio:${path}`, () => dataAudioUrl(path), { force });
}

export function fetchCategorySharedData(category, filename, { force = false } = {}) {
  return fetchJson(
    `locale-shared:${category}:${filename}`,
    () => categorySharedUrl(category, filename),
    { force }
  );
}

export function fetchExamData(homeLang, path, { force = false } = {}) {
  const home = homeLang || DEFAULT_SKILL_HOME;
  return fetchJson(`exam:${home}:${path}`, () => examDataUrl(home, path), { force });
}

function fetchJson(cacheKey, urlForPath, { force = false } = {}) {
  const now = Date.now();
  const cached = memoryCache.get(cacheKey);
  if (!force && cached && now - cached.writtenAt < MEMORY_TTL_MS) {
    return Promise.resolve(cached.value);
  }
  if (!force && inFlight.has(cacheKey)) return inFlight.get(cacheKey);

  const request = fetchWithRetry(urlForPath())
    .then((response) => response.json())
    .then((value) => {
      memoryCache.set(cacheKey, { value, writtenAt: Date.now() });
      return value;
    })
    .finally(() => {
      if (inFlight.get(cacheKey) === request) inFlight.delete(cacheKey);
    });
  inFlight.set(cacheKey, request);
  return request;
}

function normalizedLocale(locale) {
  const code = String(locale || 'en').trim().toLowerCase();
  return /^[a-z]{2,3}$/.test(code) ? code : 'en';
}

export async function fetchDataWithEnglishFallback(pathForLocale, locale, options) {
  const code = normalizedLocale(locale);
  try {
    return await fetchData(pathForLocale(code), options);
  } catch (error) {
    if (code === 'en') throw error;
    return fetchData(pathForLocale('en'), options);
  }
}

export async function fetchTextWithEnglishFallback(pathForLocale, locale) {
  const code = normalizedLocale(locale);
  try {
    return await fetchWithRetry(dataUrl(pathForLocale(code))).then((response) => response.text());
  } catch (error) {
    if (code === 'en') throw error;
    return fetchWithRetry(dataUrl(pathForLocale('en'))).then((response) => response.text());
  }
}

export function clearDataMemoryCache(path) {
  if (path) {
    memoryCache.delete(path);
    inFlight.delete(path);
  } else {
    memoryCache.clear();
    inFlight.clear();
  }
}
